import React, { useState, useCallback, useEffect } from 'react'
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom'
import Swal from 'sweetalert2'
import type { TenantThemeConfig } from '../types/theme'
import { normalizeThaiMobile } from '../services/registrationService'
import {
  persistModernRegisterCompleteFlowState,
  type ModernRegisterCompleteFlowState,
} from '@huay/shared/registerFlowState'

const PIN_LENGTH = 6
const DIGITS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 0]

type PasscodeState = { fromRegistration?: boolean; phone?: string; sessionId?: string; username?: string }

/** หน้า Passcode — ใช้เฉพาะ flow สมัครสมาชิกเก่าเท่านั้น; flow login ถูกย้ายไปหน้า /login แล้ว */
interface ModernPasscodePageProps {
  themeConfig?: TenantThemeConfig | null
}

const ModernPasscodePage: React.FC<ModernPasscodePageProps> = ({ themeConfig }) => {
  const location = useLocation()
  const navigate = useNavigate()
  const state = (location.state ?? {}) as PasscodeState
  const fromRegistration = state?.fromRegistration === true
  const regPhone = state?.phone ?? ''
  const regSessionId = state?.sessionId ?? ''
  const regUsername = state?.username ?? ''

  // โหมดสมัคร: ใส่ 2 ครั้ง
  const [regStep, setRegStep] = useState<'first' | 'confirm'>('first')
  const [pinFirst, setPinFirst] = useState<string[]>(Array(PIN_LENGTH).fill(''))
  const [pinConfirm, setPinConfirm] = useState<string[]>(Array(PIN_LENGTH).fill(''))
  const [regSubmitting, setRegSubmitting] = useState(false)

  useEffect(() => {
    if (!themeConfig) return
    if (fromRegistration) {
      if (!regPhone || !regSessionId) {
        navigate('/register', { replace: true })
      } else {
        const completeState: ModernRegisterCompleteFlowState = {
          fromOtp: true,
          phone: normalizeThaiMobile(regPhone),
          sessionId: regSessionId,
          ...(regUsername && regUsername.trim() ? { username: regUsername.trim() } : {}),
        }
        persistModernRegisterCompleteFlowState(completeState)
        navigate('/register/complete', {
          replace: true,
          state: completeState,
        })
      }
    }
  }, [fromRegistration, regPhone, regSessionId, regUsername, navigate, themeConfig])

  const addDigit = useCallback(
    (d: number, target: 'first' | 'confirm') => {
      if (target === 'first') {
        if (regSubmitting) return
        setPinFirst((prev) => {
          const i = prev.findIndex((c) => c === '')
          if (i === -1) return prev
          const next = [...prev]
          next[i] = String(d)
          return next
        })
      } else {
        if (regSubmitting) return
        setPinConfirm((prev) => {
          const i = prev.findIndex((c) => c === '')
          if (i === -1) return prev
          const next = [...prev]
          next[i] = String(d)
          return next
        })
      }
    },
    [regSubmitting]
  )

  const backspace = useCallback(
    (target: 'first' | 'confirm') => {
      if (target === 'first') {
        if (regSubmitting) return
        setPinFirst((prev) => {
          const i = prev.map((c) => c !== '').lastIndexOf(true)
          if (i === -1) return prev
          const next = [...prev]
          next[i] = ''
          return next
        })
      } else {
        if (regSubmitting) return
        setPinConfirm((prev) => {
          const i = prev.map((c) => c !== '').lastIndexOf(true)
          if (i === -1) return prev
          const next = [...prev]
          next[i] = ''
          return next
        })
      }
    },
    [regSubmitting]
  )

  const handleRegNext = useCallback(() => {
    if (pinFirst.every((c) => c !== '')) setRegStep('confirm')
  }, [pinFirst])

  const handleRegSubmit = useCallback(async () => {
    const p1 = pinConfirm.join('')
    const p2 = pinFirst.join('')
    if (p1.length !== PIN_LENGTH || p2.length !== PIN_LENGTH) return
    if (p1 !== p2) {
      await Swal.fire({
        icon: 'warning',
        title: 'รหัสไม่ตรงกัน',
        text: 'กรุณาใส่รหัสผ่านให้ตรงกันทั้ง 2 ครั้ง',
      })
      setPinConfirm(Array(PIN_LENGTH).fill(''))
      return
    }
    if (!themeConfig?.tenantId) return
    setRegSubmitting(true)
    try {
      // หลังยืนยัน passcode แล้ว ไปหน้า complete (กรอกชื่อ-นามสกุล, ธนาคาร, บัญชี)
      const normPhone = normalizeThaiMobile(regPhone)
      const completeState: ModernRegisterCompleteFlowState = {
        fromPasscode: true,
        phone: normPhone,
        sessionId: regSessionId,
        password: p1,
        ...(regUsername && regUsername.trim() ? { username: regUsername.trim() } : {}),
      }
      persistModernRegisterCompleteFlowState(completeState)
      navigate('/register/complete', {
        replace: true,
        state: completeState,
      })
    } catch (e) {
      await Swal.fire({
        icon: 'error',
        title: 'เกิดข้อผิดพลาด',
        text: (e as Error)?.message ?? 'กรุณาลองใหม่อีกครั้ง',
      })
      setPinConfirm(Array(PIN_LENGTH).fill(''))
    } finally {
      setRegSubmitting(false)
    }
  }, [pinFirst, pinConfirm, themeConfig?.tenantId, regPhone, regSessionId, regUsername, navigate])

  const isRegFirstFull = pinFirst.every((c) => c !== '')
  const isRegConfirmFull = pinConfirm.every((c) => c !== '')

  if (fromRegistration) {
    return (
      <div className="wrap_login" style={{ maxWidth: 600 }}>
        <div className="container">
          <div className="light_sd border_10 p-3 p-md-4 bg_op_secondary bg_blur">
            <div className="c_tertiary text-center mb-3">
              <h3 className="">ตั้งรหัสผ่าน (PIN)</h3>
              <p className="bg_gd_quaternary c_tertiary border_5 p-1 mx-auto" style={{ width: 'fit-content' }}>
                ระบุรหัสผ่านตัวเลขอย่างน้อย 6 หลัก <br />
                ใส่ 2 ครั้งเพื่อยืนยัน (ใช้เข้าสู่ระบบ)
              </p>
            </div>

            {regStep === 'first' ? (
              <>
                <h5 className="c_tertiary text-center font_md my-3">รหัสผ่านครั้งที่ 1</h5>
                <div className="pin_fields mb-3">
                  {pinFirst.map((char, i) => (
                    <div key={i} className="pin_fields_item">
                      <span>{char || ''}</span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <>
                <h5 className="c_tertiary text-center font_md my-3">รหัสผ่านครั้งที่ 2 (ยืนยัน)</h5>
                {regSubmitting && (
                  <p className="c_tertiary text-center small mb-2">กำลังสมัครสมาชิก...</p>
                )}
                <div className="pin_fields mb-3">
                  {pinConfirm.map((char, i) => (
                    <div key={i} className="pin_fields_item">
                      <span>{char || ''}</span>
                    </div>
                  ))}
                </div>
              </>
            )}

            <div className="num_fields px-0 px-md-4 py-3 py-md-4">
              {DIGITS.slice(0, 9).map((n) => (
                <div
                  key={n}
                  role="button"
                  tabIndex={0}
                  className="num_fields_item hh_hover"
                  onClick={() => addDigit(n, regStep === 'first' ? 'first' : 'confirm')}
                  onKeyDown={(e) => e.key === 'Enter' && addDigit(n, regStep === 'first' ? 'first' : 'confirm')}
                >
                  {n}
                </div>
              ))}
              <div className="" />
              <div
                role="button"
                tabIndex={0}
                className="num_fields_item hh_hover"
                onClick={() => addDigit(0, regStep === 'first' ? 'first' : 'confirm')}
                onKeyDown={(e) => e.key === 'Enter' && addDigit(0, regStep === 'first' ? 'first' : 'confirm')}
              >
                0
              </div>
              <div
                role="button"
                tabIndex={0}
                className="num_fields_item hh_hover bg_gd_secondary c_tertiary"
                onClick={() => backspace(regStep === 'first' ? 'first' : 'confirm')}
                onKeyDown={(e) => e.key === 'Enter' && backspace(regStep === 'first' ? 'first' : 'confirm')}
              >
                <i className="fas fa-backspace" />
              </div>
            </div>

            {regStep === 'first' && (
              <button
                type="button"
                className="link_pmr box_row justify-content-center font_rg bg_primary border_10 ins_sd w-100 border-0 text-decoration-none mt-3"
                onClick={handleRegNext}
                disabled={!isRegFirstFull}
              >
                <span className="c_tertiary">ถัดไป</span>
              </button>
            )}

            {regStep === 'confirm' && (
              <div className="d-flex gap-2 mt-3 mb-3">
                <button
                  type="button"
                  className="link_pmr box_row justify-content-center font_rg bg_gd_tertiary border_10 ins_sd flex-grow-1 border-0 text-decoration-none"
                  onClick={() => {
                    setRegStep('first')
                    setPinConfirm(Array(PIN_LENGTH).fill(''))
                  }}
                  disabled={regSubmitting}
                >
                  <span className="c_tertiary">ย้อนกลับ</span>
                </button>
                <button
                  type="button"
                  className="link_pmr box_row justify-content-center font_rg bg_primary border_10 ins_sd flex-grow-1 border-0 text-decoration-none"
                  onClick={handleRegSubmit}
                  disabled={!isRegConfirmFull || regSubmitting}
                >
                  <span className="c_tertiary">{regSubmitting ? 'กำลังดำเนินการ...' : 'ถัดไป →'}</span>
                </button>
              </div>
            )}

            <Link
              to="/register"
              className="link_pmr box_row justify-content-center font_rg bg_gd_tertiary border_10 ins_sd text-decoration-none mt-3"
            >
              <div className="d-flex align-items-center ft_sd c_tertiary font_md">
                <i className="fas fa-angle-double-left me-2" />
                <span>กลับไปสมัครสมาชิก</span>
              </div>
            </Link>
          </div>
        </div>
        <div className="bg_show" />
      </div>
    )
  }

  return <Navigate to="/login" replace />
}

export default ModernPasscodePage
